import React from 'react';
import { Column, Container, Row, Spacer } from 'src/components-v2/Layout';
import Heading1 from './Heading';
import Heading2 from './Subheading';
import PlanCard from './PlanCard';
import Stack from 'threads5/Stack';
import Box from 'src/components-v2/Layout/Box/Box';
import { Image } from 'src/components-v2/Media';
import useWorkerGuilloche from 'src/hooks/useWorkerGuilloche';
import { useLazyGuilloche } from 'src/hooks/useLazyGuilloche';

const Hero = () => {
  const canvas1 = React.useRef<HTMLCanvasElement>(null);
  const { guilloche, isReady } = useLazyGuilloche({
    pattern: 'dune',
    canvasRef: canvas1,
  });
  useWorkerGuilloche({
    canvasRef: canvas1,
    svgPaths: guilloche?.paths,
    svgWidth: guilloche?.width,
    svgHeight: guilloche?.height,
    intensity: 0.375,
    speed: 0.18,
    horizontalWave: true,
    verticalWave: true,
    zAxisWave: true,
    x: {
      xs: 1,
    },
    y: {
      lg: 10,
      xl: -1,
      xxl: 0,
    },
    scale: {
      xs: 2,
    },
    rotate: {
      xs: 10,
    },
    disableAnimation: !isReady,
  });
  return (
    <Container
      sx={{
        pt: '24px',
        overflow: 'hidden',
        position: {
          xs: 'relative',
          lg: 'unset',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: {
            xs: '-100px',
            sm: 0,
          },
          right: {
            xs: '-200px',
            sm: '-150px',
            lg: 0,
          },
          width: '517px',
          height: '729px',
          scale: {
            xs: 0.75,
            sm: 1,
          },
        }}
      >
        <Image
          src='https://images.ctfassets.net/ss5kfr270og3/11T52PblXsuPv6DZ55GvYV/35a36608121c0400c90d6306bd1b8ec9/PiggyBankBackground.png?fm=webp&q=1'
          alt='piggy bank'
          width='1034px'
          height='1458px'
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: {
            xs: '600px',
            xl: '750px',
          },
          left: {
            xs: '-50%',
            lg: 0,
          },
          width: {
            xs: '200%',
            lg: '100%',
            xl: '100%',
          },
        }}
      >
        <canvas
          ref={canvas1}
          style={{
            width: '100%',
            height: '100%',
            background: 'transparent',
          }}
        />
      </Box>
      <Spacer sx={{ height: { xs: '80px' } }} />
      <Spacer sx={{ height: { xs: '90px' } }} />
      <Row>
        <Column xs={24} lgOffset={2} lg={15}>
          <Heading1 />
          <Spacer sx={{ height: { xs: '18px', lg: '32px' } }} />
          <Heading2 />
          <Spacer sx={{ height: { xs: '60px' } }} />
        </Column>
      </Row>
      <Row>
        <Column xs={24} lgOffset={2} lg={20}>
          <Stack
            direction='row'
            gap={3}
            sx={{
              justifyContent: 'center',
              flexWrap: {
                xs: 'wrap',
                lg: 'nowrap',
              },
            }}
          >
            <PlanCard
              title='Pay as You Go'
              subtitle='Best for individuals and developers'
              description='Start with our month-to-month plan:  unlimited usage, no commitments upfront—only pay for what you use.'
              keyFeatures={[
                'Access to GA products',
                'Unlimited live API calls',
                'Link customization',
                'Standard Pricing',
              ]}
              color='#088181'
              ctaText='Request access'
              buttonSx={{
                background: 'linear-gradient(93deg, #00819A 50%, #10D1B7 100%)',
                backgroundSize: '200% 100%',
                backgroundPosition: '100% 0',
                '&:hover, &:focus': {
                  backgroundPosition: '70% 0',
                },
              }}
              href='https://dashboard.plaid.com/signup'
            />
            <PlanCard
              title='Growth'
              subtitle='Best for small teams and startups '
              description='Scale with a 12-month commitment:  access discounts, platform support package, and account management.'
              keyFeatures={[
                'Everything in Pay as You Go',
                'Discounted product rates',
                'Platform support package',
                'Single Sign-On for Plaid Dashboard',
              ]}
              color='#BB33D7'
              ctaText='Talk with our team'
              buttonSx={{
                background: 'linear-gradient(91deg, #3F2977 50%, #D45AED 100%)',
                backgroundSize: '200% 100%',
                backgroundPosition: '100% 0',
                '&:hover, &:focus': {
                  backgroundPosition: '70% 0',
                },
              }}
              href='https://plaid.com/contact/'
            />
            <PlanCard
              title='Custom'
              subtitle='Best for businesses that need to scale'
              description='Create a flexible plan with custom solutions, volume-based pricing, and support for regulated industries.'
              keyFeatures={[
                'Everything in Growth',
                'Access to beta products',
                'Volume discounts',
                'Integration assistance',
                'Premium support',
                'Account Management',
              ]}
              color='#0B7BBC'
              ctaText='Talk with our team'
              buttonSx={{
                background: 'linear-gradient(93deg, #633ECB 50%, #0DADF2 100%)',
                backgroundSize: '200% 100%',
                backgroundPosition: '100% 0',
                '&:hover, &:focus': {
                  backgroundPosition: '70% 0',
                },
              }}
              href='https://plaid.com/contact/'
            />
          </Stack>
        </Column>
      </Row>
    </Container>
  );
};

export default Hero;
